import { Button } from "@nextui-org/react"
import * as React from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import PlusWhiteIcon from "@/assets/icons/plus-white.svg"
import Tooltip from "@/components/Tooltip/Tooltip"
import { formatTaxRegistrationId } from "@/utils/format"
import { formatBusinessRegistrationNumber, formatPhoneNumber } from "@/utils/formats"
import {
  FormField,
  FormRequired,
  FormLabel,
  FormItem,
  FormControl,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import TextArea from "@/components/TextArea/TextArea"

import { INIT_STAFF } from "./CustomerForm.constants"
import { CustomerFormValues } from "./CustomerForm.types"

export const CustomerForm = React.forwardRef<HTMLDivElement, unknown>((_, ref) => {
  const { t } = useTranslation()

  const form = useFormContext<CustomerFormValues>()

  const staffs = useWatch<CustomerFormValues, "staffs">({
    control: form.control,
    name: "staffs",
    defaultValue: [],
  })

  const addStaff = () => {
    const nextStaffs = [...(staffs || []), INIT_STAFF]
    form.setValue("staffs", nextStaffs)
  }

  const removeStaff = (index: number) => {
    form.clearErrors(`staffs.${index}.email`)
    form.clearErrors(`staffs.${index}.displayName`)
    form.clearErrors(`staffs.${index}.telephone`)
    const nextStaffs = (staffs || []).filter((_, i) => i !== index)
    form.setValue("staffs", nextStaffs)
  }

  return (
    <div ref={ref} className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        <div>
          <h2>{t("customer.form.required")}</h2>
          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-12">
              <FormField
                control={form.control}
                name="organizationName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("customer.form.organizationName")}
                      <FormRequired>*</FormRequired>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t("customer.form.organizationName")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="col-span-12 md:col-span-6">
              <FormField
                control={form.control}
                name="representativeName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("customer.form.representativeName")}
                      <FormRequired>*</FormRequired>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t("customer.form.representativeName")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="col-span-12 md:col-span-6">
              <FormField
                control={form.control}
                name="businessRegistrationNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("customer.form.businessRegistrationNumber")}
                      <FormRequired>*</FormRequired>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={formatBusinessRegistrationNumber(field.value || "")}
                        placeholder={t(
                          "customer.form.businessRegistrationNumber.placeholder",
                        )}
                        onChange={e => {
                          form.setValue(
                            "businessRegistrationNumber",
                            formatBusinessRegistrationNumber(e.target.value),
                          )
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        <div>
          <div>
            <h2 className="mt-10">{t("customer.form.additional")}</h2>
            <div className="grid grid-cols-12 gap-5">
              <div className="col-span-12 md:col-span-9">
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("customer.form.address")}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t("customer.form.address.placeholder")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-12 md:col-span-3">
                <div className="mb-2 flex items-center">
                  <FormLabel className="!mb-0">
                    {t("customer.form.taxRegistrationId")}
                  </FormLabel>
                  <Tooltip
                    content={t("customer.form.taxRegistrationId.tooltip")}
                    placement="top"
                    offset={3}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="taxRegistrationId"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t(
                            "customer.form.taxRegistrationId.placeholder",
                          )}
                          onChange={e => {
                            form.setValue(
                              "taxRegistrationId",
                              formatTaxRegistrationId(e.target.value),
                            )
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <FormField
                  control={form.control}
                  name="businessType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("customer.form.businessType")}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t("customer.form.businessType.placeholder")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <FormField
                  control={form.control}
                  name="businessItem"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("customer.form.businessItem")}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t("customer.form.businessItem.placeholder")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <div>
          <h2 className="mt-10">{t("customer.form.memo")}</h2>
          <FormField
            control={form.control}
            name="memo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("customer.form.memo")}</FormLabel>
                <FormControl>
                  <TextArea
                    {...field}
                    placeholder={t("customer.form.memo.placeholder")}
                    description={(field.value?.length || 0) + "/150"}
                    minRows={4}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <h2 className="mt-10">{t("customer.form.staff")}</h2>
        {staffs?.map((_, index) => (
          <div key={index} className="relative mt-4 rounded-lg border px-5 py-4">
            <div className="flex">
              <h3>{t("customer.form.staff.title", { index: index + 1 })}</h3>
              <div className="ml-auto">
                {index > 0 && (
                  <Button
                    size="sm"
                    variant="bordered"
                    className="white-btn"
                    onClick={() => removeStaff(index)}
                  >
                    {t("customer.form.staff.delete")}
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-12 gap-5">
              <div className="col-span-12">
                <FormField
                  control={form.control}
                  name={`staffs.${index}.email`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("customer.form.staff.email")}
                        <FormRequired>*</FormRequired>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t("customer.form.staff.email.placeholder")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <FormField
                  control={form.control}
                  name={`staffs.${index}.displayName`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("customer.form.staff.name")}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t("customer.form.staff.name.placeholder")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <FormField
                  control={form.control}
                  name={`staffs.${index}.telephone`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("customer.form.staff.telephone")}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t("customer.form.staff.telephone.placeholder")}
                          value={formatPhoneNumber(field.value || "")}
                          onChange={e => {
                            const formattedValue = formatPhoneNumber(e.target.value)
                            form.setValue(`staffs.${index}.telephone`, formattedValue)
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
        ))}

        <div className="text-center">
          <Button
            color="secondary"
            className="-mb-2 mt-4"
            onClick={addStaff}
            isDisabled={(staffs ?? []).length >= 5}
            startContent={<PlusWhiteIcon />}
          >
            {t("customer.form.staff.add")}
          </Button>
        </div>
      </div>
    </div>
  )
})
CustomerForm.displayName = "CustomerForm"

type CustomerFormSubmitterProps = {
  onCancel: () => void
  isEditMode: boolean
  isLoading: boolean
}

export function CustomerFormSubmitter({
  onCancel,
  isEditMode,
  isLoading,
}: CustomerFormSubmitterProps) {
  const { t } = useTranslation()

  return (
    <div className="flex gap-x-5 self-stretch">
      <Button size="lg" className="w-full bg-tertiary" onClick={onCancel}>
        {t("customer.form.cancel")}
      </Button>
      <Button
        type="submit"
        size="lg"
        color="primary"
        className="w-full"
        isLoading={isLoading}
      >
        {!isEditMode
          ? t("customer.form.submit.create")
          : t("customer.form.submit.edit")}
      </Button>
    </div>
  )
}
